import React from 'react';
import { SHADCN, R, S, FS } from '../theme';

// ── <Button> — Shadcn dark ──────────────────────────────────────────────────
// Variants :
//   primary — fond peach, texte dark (action principale)
//   ghost   — fond white@10%, texte fg, filet white@12.5%
//   pill    — toggle/filtre (active = primary, inactive = ghost-style)
//   sidebar — nav latérale : transparent / sidebarAccent à hover/active,
//             texte mutedFg → fg, alignement gauche, full width
//
// Sizes :
//   md (default) — 10×16
//   sm           — 8×14
//   sidebar      — 9×12, fontWeight régulière

const SIZES = {
  md:      { padding: `10px ${S[4]}px`, fontSize: FS.body },
  sm:      { padding: `${S[2]}px 14px`, fontSize: FS.body },
  sidebar: { padding: `9px ${S[3]}px`,  fontSize: FS.body },
};

function styleFor({ variant, size, active, isDisabled, fullWidth, style }) {
  const s = SIZES[size] || SIZES.md;

  const isSidebar = variant === 'sidebar';
  const base = {
    fontFamily: SHADCN.font,
    fontWeight: isSidebar ? (active ? 500 : 400) : 600,
    fontSize: s.fontSize,
    lineHeight: 1.4,
    letterSpacing: isSidebar ? 'normal' : '-0.01em',
    borderRadius: R.md,
    padding: s.padding,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    transition: `background ${SHADCN.durFast} ${SHADCN.easeOut}, color ${SHADCN.durFast} ${SHADCN.easeOut}, border-color ${SHADCN.durBase} ${SHADCN.easeOut}, opacity ${SHADCN.durBase} ${SHADCN.easeOut}, transform 90ms ${SHADCN.easeOut}`,
    opacity: isDisabled ? 0.5 : 1,
    width: (fullWidth || isSidebar) ? '100%' : undefined,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: isSidebar ? 'flex-start' : 'center',
    textAlign: isSidebar ? 'left' : undefined,
    gap: isSidebar ? S[3] : S[2],
    whiteSpace: 'nowrap',
    border: '1px solid transparent',
  };

  const variants = {
    primary: {
      background: SHADCN.primary,
      color: SHADCN.primaryFg,
      border: `1px solid ${SHADCN.primary}`,
    },
    ghost: {
      background: SHADCN.sidebarAccent,
      color: SHADCN.fg,
      border: `1px solid ${SHADCN.border}`,
    },
    pill: {
      background: active ? SHADCN.primary : 'transparent',
      color: active ? SHADCN.primaryFg : SHADCN.mutedFg,
      border: `1px solid ${active ? SHADCN.primary : SHADCN.border}`,
    },
    sidebar: {
      background: active ? SHADCN.sidebarAccent : 'transparent',
      color: active ? SHADCN.fg : SHADCN.mutedFg,
      border: '1px solid transparent',
    },
  };

  return { ...base, ...variants[variant], ...style };
}

export default function Button({
  children,
  variant = 'primary',
  size = 'md',
  active = false,
  loading = false,
  loadingText,
  disabled = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  type = 'button',
  as: Tag = 'button',
  style,
  ...rest
}) {
  const isDisabled = disabled || loading;

  const elementProps = Tag === 'button'
    ? { type, disabled: isDisabled }
    : { 'aria-disabled': isDisabled || undefined };

  return (
    <Tag
      {...elementProps}
      {...rest}
      data-sonore-btn=""
      style={{
        ...styleFor({ variant, size, active, isDisabled, fullWidth, style }),
        textDecoration: 'none',
      }}
      onPointerEnter={(e) => {
        if (isDisabled || e.pointerType === 'touch') return;
        if (variant === 'primary') e.currentTarget.style.background = SHADCN.primaryDark;
        else if (variant === 'ghost') e.currentTarget.style.background = SHADCN.muted;
        else if (variant === 'pill' && !active) e.currentTarget.style.background = SHADCN.sidebarAccent;
        else if (variant === 'sidebar' && !active) {
          e.currentTarget.style.background = SHADCN.sidebarAccent;
          e.currentTarget.style.color = SHADCN.fg;
        }
      }}
      onPointerLeave={(e) => {
        if (variant === 'primary') e.currentTarget.style.background = SHADCN.primary;
        else if (variant === 'ghost') e.currentTarget.style.background = SHADCN.sidebarAccent;
        else if (variant === 'pill') e.currentTarget.style.background = active ? SHADCN.primary : 'transparent';
        else if (variant === 'sidebar' && !active) {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = SHADCN.mutedFg;
        }
      }}
    >
      {leftIcon}
      {loading ? (loadingText || 'Chargement…') : children}
      {rightIcon}
    </Tag>
  );
}
